import {SpriteButtonView} from '../../ui_canvas_engine/SpriteButtonView'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {ActionsThatCanBeCancelled, BeginnerHintFormat, GameAction, TradeEmbargoControllerReader} from '@colonist/game-base-types'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {FloatingText, FloatingTextDirection} from '../../game_ui_view_objects/FloatingText'
import {UIGameMechanic} from '../../ui_game/UIGameEventController'
import {ArrayUtils} from '@colonist/utils'
import {UIGameTradeWantedCardSelectorView} from './UIGameTradeWantedCardSelectorView'
import {UIGameTradeProposalView} from './UIGameTradeProposalView'
import {UIGameTradeOfferedCardSelectorView} from './UIGameTradeOfferedCardSelectorView'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {Sound} from '../../Sound'
import {UIGameBeginnerHintView} from '../../ui_game/ui_game_beginner_hints/UIGameBeginnerHintView'
import {logError} from '../../ui_utils/Utils'
import {UIGameShakeAnimation} from '../../ui_canvas_engine/animation/UIGameShakeAnimation'
import {UIGameFlickerAnimation} from '../../ui_canvas_engine/animation/UIGameFlickerAnimation'
import {GameUIToolTips} from '../../GameUIToolTips'
import {StorageWrapper} from '../../StorageWrapper'
import {UIGameTradeAnyCardDisplayController} from './UIGameTradeAnyCardDisplayController'
import {CardEnum, PlayerColor, TradeId, TurnState} from '@colonist/game-states'
import {TradeActions, TradeCreationData, TradeCreationDataValidator, TradeError, TradeErrorHighlightLocationEnum, TradeUtils, TradeValidator} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGameTradeCreationController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager

    private readonly activeOffers: Map<TradeId, TradeValidator>
    private readonly tradesClosedThisTurn: Map<TradeId, TradeValidator>

    private container: SectionBgView
    private bankCheckButton: SpriteButtonView
    private opponentsCheckButton: SpriteButtonView
    private checkButtonAnimation: UIGameShakeAnimation
    private cancelButton: SpriteButtonView

    private suggestedBeginnerTrade?: TradeCreationData | undefined
    private beginnerHintView?: UIGameBeginnerHintView | undefined
    private isBeginnerMode: boolean = false

    wantedCardSelectorView: UIGameTradeWantedCardSelectorView
    proposalView: UIGameTradeProposalView
    offeredCardSelectorView: UIGameTradeOfferedCardSelectorView

    inBankTradeMode: boolean

    private wasPreviouslyInBankTradeModeBeforeCounterOffer: boolean | undefined

    private counterOfferTradeID: TradeId | undefined
    private readonly anyCardDisplayController: UIGameTradeAnyCardDisplayController

    private shouldBankTradeModeBeToggledByDefault: boolean

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.activeOffers = new Map()
        this.tradesClosedThisTurn = new Map()

        this.createTradeView()
        this.container.parent.removeChild(this.container)
        this.anyCardDisplayController = new UIGameTradeAnyCardDisplayController(this.wantedCardSelectorView, this.offeredCardSelectorView)
        const isMatchmakingGame = this.uiGameManager.gameController.gameDetails.matchmakingMatchType != undefined
        this.shouldBankTradeModeBeToggledByDefault = this.uiGameManager.gameController.is1v1() && isMatchmakingGame
    }

    private createTradeView() {
        this.container = this.getContainer()
        const shouldRenderForMobileApp = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        const shouldRenderForTabletApp = UIUtilsEjsVariables.isUserOnTabletApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI

        const canvas = this.uiGameManager.canvasController
        const buttonSize = canvas.itemSize
        const buttonX = this.container.width - buttonSize
        const buttonY = shouldRenderForMobileApp ? this.container.height : this.container.height - buttonSize

        const bankCheckTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade_bank_check)
        this.bankCheckButton = new SpriteButtonView(buttonX, buttonY - buttonSize * 2, buttonSize, buttonSize, this.container, undefined, 1)
        this.bankCheckButton.addCenteredIcon(bankCheckTexture)
        this.bankCheckButton.inactivateOpacity()
        this.bankCheckButton.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [{key: 'strings:game.tooltips.tradeWithBank'}])

        const opponentsCheckTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade_opponents_check)
        this.opponentsCheckButton = new SpriteButtonView(buttonX, buttonY - buttonSize, buttonSize, buttonSize, this.container, undefined, 1)
        this.opponentsCheckButton.addCenteredIcon(opponentsCheckTexture)
        this.opponentsCheckButton.inactivateOpacity()
        this.opponentsCheckButton.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [{key: 'strings:game.tooltips.tradeWithOpponents'}])

        const cancelButtonX = buttonX
        const cancelButtonY = buttonY
        const xTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_x)
        this.cancelButton = new SpriteButtonView(cancelButtonX, cancelButtonY, buttonSize, buttonSize, this.container)
        this.cancelButton.addCenteredIcon(xTexture)

        const width = this.container.width - canvas.itemSize
        const height = canvas.itemSize
        this.wantedCardSelectorView = new UIGameTradeWantedCardSelectorView(this.uiGameManager, 0, 0, width, height, this.container)

        const tradeProposalViewHeight = canvas.itemSize * 2
        const tradeProposalViewY = this.wantedCardSelectorView.height + canvas.margin
        const onCardCountChangeAction = () => {
            this.updateDisabledCards()
            this.updateCheckButtonsOpacity()
            this.updateCardsForBeginnerAnimation()
        }

        const lastSavedTargetColors = this.proposalView?.tradingTargetColors
        this.proposalView = new UIGameTradeProposalView(this.uiGameManager, 0, tradeProposalViewY, width, tradeProposalViewHeight, this.container, onCardCountChangeAction, lastSavedTargetColors)

        const tradePlayerViewHeight = shouldRenderForMobileApp ? canvas.itemSize * 1.2 : canvas.itemSize
        const tradePlayerViewY = this.proposalView.y + this.proposalView.height
        const tradePlayerViewWidth = shouldRenderForMobileApp ? canvas.appW : width
        this.offeredCardSelectorView = new UIGameTradeOfferedCardSelectorView(this.uiGameManager, 0, tradePlayerViewY, tradePlayerViewWidth, tradePlayerViewHeight, this.container)

        this.setButtonActions()
        if(shouldRenderForMobileApp) this.adjustContainerY()
        this.anyCardDisplayController?.setCardSelectorViews(this.wantedCardSelectorView, this.offeredCardSelectorView)
    }

    private adjustContainerY() {
        const shouldRenderForTabletApp = UIUtilsEjsVariables.isUserOnTabletApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        const canvas = this.uiGameManager.canvasController
        const safeAreaHeight = (StorageWrapper.getSafeAreaInsetBottom() ?? 0) * 0.5
        if(shouldRenderForTabletApp) {
            this.container.y = canvas.appH - this.container.height - safeAreaHeight
            return
        }
        this.container.y = canvas.appH - this.container.height - safeAreaHeight
    }

    protected abstract getContainer(): SectionBgView

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated: this.clearTradeWindowIfMyCardsAreUpdated(); break
            case UIGameEventEnums.GameStateUpdated: this.gameStateUpdated(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.MerchantStateUpdated: this.proposalView.updateMerchantView(data); break
            case UIGameEventEnums.MerchantBankTradeRatioActive: this.proposalView.addMerchantToCardView(data); break
            case UIGameEventEnums.NewTradeOfferReceived: this.onTradeReceived(data); break
            case UIGameEventEnums.RemoveTradeOffer: this.clearTrade(data); break
            case UIGameEventEnums.TradeResetStateAtEndOfTurn: this.resetTradesAtEndOfTurn(); break
            case UIGameEventEnums.EmbargoStatusUpdated: this.updateCheckButtonsOpacity(); this.updateDisabledCards(); break
            case UIGameEventEnums.BeginnerHintReceived: this.onBeginnerHintReceived(data); break
            case UIGameEventEnums.ShowTradeUI: this.onShowTradeUI(); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.hideTradeUI(); break
            default: break
        }
    }

    private onCanvasResize() {
        const isHidden = this.container.parent === null
        const playersToTradeWith = this.proposalView.tradingTargetColors
        const proposalPlayerCards = this.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()
        const proposalWantedCards = this.proposalView.wantedCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()

        this.container.destroy()
        this.createTradeView()

        if(isHidden) this.container.parent.removeChild(this.container)
        else this.resetTradeWindow(proposalPlayerCards, proposalWantedCards, playersToTradeWith)

        const isBankTradeRatioVisible = this.wantedCardSelectorView.isBankTradeRatioVisible
        this.wantedCardSelectorView.updateBankTradeRatioHelpContent(isBankTradeRatioVisible)
    }

    private updateCardRatios() {
        this.proposalView.offeredCardPlayerInfo.cardContainer.updateAllCardRatios()
        this.offeredCardSelectorView.cardsContainer.updateAllCardRatios()
        this.wantedCardSelectorView.updateBankTradeRatioHelpContent()
    }

    private onTradeReceived(tradeId: TradeId) {
        const tradeState = this.uiGameManager.gameController.tradeState[tradeId]
        if(tradeState == undefined) return

        const currentTurnPlayer = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
        const playerStates = this.uiGameManager.gameController.getPlayerValidators()
        const embargoState = this.uiGameManager.gameController.tradeState.embargoState
        const tradeStateController = new TradeValidator(tradeState, currentTurnPlayer, playerStates, embargoState)

        this.activeOffers.set(tradeId, tradeStateController)
        this.updateDisabledCards()
        this.updateCheckButtonsOpacity()
    }

    private onBeginnerModeStart() {
        this.isBeginnerMode = true
        this.shouldBankTradeModeBeToggledByDefault = false
        this.anyCardDisplayController.permanentlyDisableAnyCard()
    }

    private clearTrade(tradeId: TradeId) {
        const tradeOffer = this.activeOffers.get(tradeId)
        if(tradeOffer == undefined) return
        this.tradesClosedThisTurn.set(tradeId, tradeOffer)
        this.activeOffers.delete(tradeId)
        this.updateDisabledCards()
        this.updateCheckButtonsOpacity()

        const wasOriginalTradeClosedWhileIWasEditingCounterOffer = this.counterOfferTradeID == tradeId
        if(wasOriginalTradeClosedWhileIWasEditingCounterOffer) {
            this.counterOfferTradeID = undefined
            this.hideTradeUI()
        }
    }

    private resetTradesAtEndOfTurn() {
        this.activeOffers.clear()
        this.tradesClosedThisTurn.clear()
    }

    private setButtonActions() {
        this.bankCheckButton.updateAction(() => {
            this.clickedCheckButton(true)
        })
        this.opponentsCheckButton.updateAction(() => {
            this.clickedCheckButton(false)
        })
        this.cancelButton.updateAction(() => {
            this.hideTradeUI()
        })
    }

    private showTradeUI(playersToTradeWith: PlayerColor) {
        if(this.container.parent != undefined) return

        this.shouldHideBankTradeUI() ? this.hideBankTradeUI() : this.showBankTradeUI()
        this.enableBankTradeModeIfEnabledBeforeCounterOffer()
        this.opponentsCheckButton.inactivateOpacity()
        this.cancelButton.activateOpacity()
        this.cancelButton.returnToOriginalPosition()

        this.anyCardDisplayController.updateAndResetCards(this.inBankTradeMode, this.inCounterOffer(), this.doesPlayerHaveCards())

        this.proposalView.tradingTargetColors = playersToTradeWith
        this.proposalView.wantedCardPlayerInfo.resetLeftAvatar(playersToTradeWith)
        if(this.isBeginnerMode) this.wantedCardSelectorView.hideBankTradeModeIcon()

        this.uiGameManager.canvasController.app.stage.addChild(this.container)
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.ShowTradeUI)
    }

    private hideTradeUI() {
        if(this.container.parent == undefined) return
        if(this.counterOfferTradeID != undefined) this.uiGameManager.socketGameSend.updateTradeResponse({id: this.counterOfferTradeID, response: TradeActions.CounterOfferCreatedOrCanceled})

        this.container.parent.removeChild(this.container)
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.CloseTradeUI)
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.PopupActive)
        GameUIToolTips.removeAllToolTips()
        this.proposalView.removeAllCards()
    }

    private enableBankTradeModeIfEnabledBeforeCounterOffer() {
        if(this.wasPreviouslyInBankTradeModeBeforeCounterOffer == undefined) return

        this.toggleBankTradeMode(this.wasPreviouslyInBankTradeModeBeforeCounterOffer)
        this.wasPreviouslyInBankTradeModeBeforeCounterOffer = undefined
    }

    private shouldHideBankTradeUI() {
        const isCounterOffer = this.counterOfferTradeID != undefined
        const notMyTurn = !this.uiGameManager.gameController.isMyTurn
        return isCounterOffer && notMyTurn
    }

    private clearTradeWindowIfMyCardsAreUpdated() {
        this.updateCardRatios()
        const newCardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor).sort()

        const oldOfferedCards = this.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()
        const oldCardsInHand = this.offeredCardSelectorView.cardsContainer.cardInventory.getAllCardEnums()
        const allOldCardsCurrentlyInHand = [...oldOfferedCards, ...oldCardsInHand].sort()
        ArrayUtils.removeElementFromArray(allOldCardsCurrentlyInHand, CardEnum.AnyResource)

        const areCardsTheSame = ArrayUtils.arraysEqual(newCardsInHand, allOldCardsCurrentlyInHand)
        if(areCardsTheSame) return

        this.clearTradeWindowCards()
    }

    private clearTradeWindowCards() {
        if(this.container.parent == undefined) return

        this.proposalView.onPlayerControllerState()
        this.proposalView.offeredCardPlayerInfo.cardContainer.removeAllCards()
        this.proposalView.wantedCardPlayerInfo.cardContainer.removeAllCards()

        this.anyCardDisplayController.updateAndResetCards(this.inBankTradeMode, this.inCounterOffer(), this.doesPlayerHaveCards())
    }

    private resetTradeWindow(offeredCards: CardEnum[], wantedCards: CardEnum[], playersToTradeWith: PlayerColor) {
        if(this.container.parent == undefined) return

        this.hideTradeUI()
        this.showTradeUI(playersToTradeWith)

        for(const card of offeredCards) {
            this.proposalView.offeredCardPlayerInfo.cardContainer.addNewCardOfType(card, false)
            if(card == CardEnum.AnyResource) continue
            this.offeredCardSelectorView.cardsContainer.removeCard(card)
        }

        for(const card of wantedCards) {
            this.proposalView.wantedCardPlayerInfo.cardContainer.addNewCardOfType(card, false)
        }
    }

    private gameStateUpdated() {
        if(this.container == undefined) return
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.Turn) return

        this.hideTradeUI()
        this.resetPreviouslySuggestedBeginnerTrade()
    }

    onClickTrade(container: PIXI.Container, offeredResources: CardEnum[] = [], wantedResources: CardEnum[] = [], isBankRightClick: boolean = false, floatingTextDirection: FloatingTextDirection = FloatingTextDirection.Up) {
        if(this.uiGameManager.gameController.isReplay) return
        if(!this.uiGameManager.gameController.isMyTurn) {
            new FloatingText({key: 'strings:game.errors.notYourTurn'}, container, floatingTextDirection)
            return
        }
        if(!ArrayUtils.elementInArray(ActionsThatCanBeCancelled.actions, this.uiGameManager.gameController.currentState.actionState)) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, container, floatingTextDirection)
            return
        }
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.SpecialBuildPhase) {
            new FloatingText({key: 'strings:game.errors.tradingDisabled'}, container, floatingTextDirection)
            return
        }
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Turn) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, container, floatingTextDirection)
            return
        }

        if(this.shouldBankTradeModeBeToggledByDefault) this.toggleBankTradeMode(true)

        this.counterOfferTradeID = undefined
        this.showTradeUI(PlayerColor.None)

        wantedResources.forEach((card) => this.proposalView.wantedCardPlayerInfo.cardContainer.addNewCardOfType(card))
        offeredResources.forEach((card) => {
            if(isBankRightClick || this.inBankTradeMode) this.offeredCardSelectorView.onCardClickBankTrade(card)
            else this.offeredCardSelectorView.onCardClickPlayersTrade(card)
        })
    }

    private updateDisabledCards() {
        // If not used by v185 delete this logic
        const disableTemporarily = true
        if(disableTemporarily) return
        this.wantedCardSelectorView.enableAllCards()
        this.offeredCardSelectorView.cardsContainer.enableAllCards()

        const proposalOfferedCards = this.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.cardViews
        const proposalOfferedCardsEnum = proposalOfferedCards.map((cardView) => cardView.cardData.cardEnum)
        this.wantedCardSelectorView.disableCards(proposalOfferedCardsEnum)

        const proposalWantedCards = this.proposalView.wantedCardPlayerInfo.cardContainer.cardInventory.cardViews
        const proposalWantedCardsEnum = proposalWantedCards.map((cardView) => cardView.cardData.cardEnum)
        this.offeredCardSelectorView.cardsContainer.disableCards(proposalWantedCardsEnum)
    }

    private updateCheckButtonsOpacity() {
        this.opponentsCheckButton.inactivateOpacity()
        this.bankCheckButton.inactivateOpacity()
        const tradeCreationDataValidator = this.getTradeDataValidator()

        const bankTradeError = tradeCreationDataValidator.getTradeCreationDataError(this.getCurrentTradeOffer(true))
        if(bankTradeError == undefined) this.bankCheckButton.activateOpacity()
        if(bankTradeError != undefined && !bankTradeError.payload?.isBankTradeButtonDisabled) this.bankCheckButton.activateOpacity()

        const playerTradeError = tradeCreationDataValidator.getTradeCreationDataError(this.getCurrentTradeOffer(false))
        if(playerTradeError == undefined) this.opponentsCheckButton.activateOpacity()
        if(playerTradeError != undefined && !playerTradeError.payload?.isPlayerTradeButtonDisabled) this.opponentsCheckButton.activateOpacity()
    }

    getCurrentTradeOffer(isBankTrade: boolean): TradeCreationData {
        return {
            creator: this.uiGameManager.gameController.myColor,
            isBankTrade: isBankTrade,
            counterOfferInResponseToTradeId: this.counterOfferTradeID,
            offeredResources: this.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums(),
            wantedResources: this.proposalView.wantedCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums(),
        }
    }

    private clickedCheckButton(isBankTrade: boolean) {
        const didIEmbargoAllPlayers = TradeEmbargoControllerReader.didPlayerEmbargoAllOtherPlayers(this.uiGameManager.gameController.tradeState.embargoState, this.uiGameManager.gameController.myColor)
        const isPlayerTrappedAfterEmbargoingAllPlayers = isBankTrade == false && didIEmbargoAllPlayers
        if(isPlayerTrappedAfterEmbargoingAllPlayers) {
            this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.TemporaryPlayerStuckInEmbargoState)
        }

        const tradeCreationDataValidator = this.getTradeDataValidator()
        const tradeCreationDataError = tradeCreationDataValidator.getTradeCreationDataError(this.getCurrentTradeOffer(isBankTrade))

        if(tradeCreationDataError != undefined) {
            this.handleAnimationOnError(tradeCreationDataError)
            const buttonView = isBankTrade ? this.bankCheckButton : this.opponentsCheckButton
            if(this.checkButtonAnimation != undefined) this.checkButtonAnimation.stop()
            this.checkButtonAnimation = new UIGameShakeAnimation(buttonView).start()
            new FloatingText(tradeCreationDataError.error, buttonView)
            if(isBankTrade) new UIGameFlickerAnimation(this.wantedCardSelectorView.bankTradeModeIcon).start()
            return
        }

        const offer = this.getCurrentTradeOffer(isBankTrade)
        if(isBankTrade) this.clearTradeWindowCards()

        this.uiGameManager.socketGameSend.createTrade(offer)
        this.resetPreviouslySuggestedBeginnerTrade()
        const isCounterOffer = this.counterOfferTradeID != undefined
        if(isCounterOffer) this.hideTradeUI()
    }

    createCounterOffer(tradeId: TradeId, receivingResources: CardEnum[], givingResources: CardEnum[]) {
        if(this.uiGameManager.gameController.isMyTurn) return this.createCounterOfferMyTurn(tradeId, receivingResources, givingResources)

        this.hideTradeUI()
        this.counterOfferTradeID = tradeId
        const playersToTradeWith = this.uiGameManager.gameController.isMyTurn ? PlayerColor.None : this.uiGameManager.gameController.currentState.currentTurnPlayerColor

        this.showTradeUI(playersToTradeWith)

        const previousBankMode = this.inBankTradeMode
        if(this.inBankTradeMode) this.toggleBankTradeMode(false)
        this.wasPreviouslyInBankTradeModeBeforeCounterOffer = previousBankMode || this.shouldBankTradeModeBeToggledByDefault

        this.wantedCardSelectorView.createCounterOffer(givingResources)
        this.offeredCardSelectorView.createCounterOffer(receivingResources)
        this.uiGameManager.socketGameSend.updateTradeResponse({id: tradeId, response: TradeActions.CounterOfferCreating})
    }

    private createCounterOfferMyTurn(tradeId: TradeId, receivingResources: CardEnum[], givingResources: CardEnum[]) {
        this.hideTradeUI()
        const playersToTradeWith = PlayerColor.None
        this.showTradeUI(playersToTradeWith)

        this.wantedCardSelectorView.createCounterOffer(givingResources)
        this.offeredCardSelectorView.createCounterOffer(receivingResources)
    }

    private hideBankTradeUI() {
        const isBankCheckButtonShown = this.bankCheckButton.parent != undefined
        if(isBankCheckButtonShown) this.container.removeChild(this.bankCheckButton)
        this.wantedCardSelectorView.hideBankTradeModeIcon()
    }

    private showBankTradeUI() {
        const isBankCheckButtonHidden = this.bankCheckButton.parent == undefined
        if(isBankCheckButtonHidden) this.container.addChild(this.bankCheckButton)
        this.wantedCardSelectorView.showBankTradeModeIcon()
    }

    private handleAnimationOnError(tradeCreationDataError: TradeError) {
        const payload = tradeCreationDataError.payload
        if(payload == undefined) return

        if(payload.highlightCards) {
            const {cardsToHighlight, locations} = payload.highlightCards
            for(const location of locations) {
                switch(location) {
                    case TradeErrorHighlightLocationEnum.ProposalWanted: this.proposalView.wantedCardPlayerInfo.cardContainer.flickerAllCardsOfType(cardsToHighlight); break
                    case TradeErrorHighlightLocationEnum.ProposalOffered: this.proposalView.offeredCardPlayerInfo.cardContainer.flickerAllCardsOfType(cardsToHighlight); break
                    case TradeErrorHighlightLocationEnum.WantedSelector: this.wantedCardSelectorView.flickerAllCardsOfTypes(cardsToHighlight); break
                    case TradeErrorHighlightLocationEnum.OfferedSelector: this.offeredCardSelectorView.cardsContainer.flickerAllCardsOfType(cardsToHighlight); break
                    default: break
                }
            }
        }

        if(payload.highlightTrade) {
            this.uiGameManager.eventController.sendEvent(payload, GameStateEnums.UIGameEventEnums.HighlightTradeOffer)
        }
    }

    private getTradeDataValidator(): TradeCreationDataValidator {
        const myColor = this.uiGameManager.gameController.myColor
        const activeResourceCardTypes = this.uiGameManager.gameState.gameModeData.activeResourceCardTypes
        const numberOfPlayers = this.uiGameManager.gameState.players.length
        const playerCards = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.myColor).resourceCards.cards
        const currentTurnPlayer = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
        const isTradeCreatorBot = false
        const bankState = this.uiGameManager.gameController.bankState
        const myBankTradeRatios = this.uiGameManager.gameController.getPlayerState(myColor).bankTradeRatiosState
        const modeSetting = this.uiGameManager.gameController.gameSettings.modeSetting
        const embargoState = this.uiGameManager.gameController.tradeState.embargoState

        return new TradeCreationDataValidator(activeResourceCardTypes,
            numberOfPlayers, playerCards, currentTurnPlayer, isTradeCreatorBot, bankState, myBankTradeRatios,
            this.uiGameManager.gameController.tradeState.activeOffers, this.uiGameManager.gameController.tradeState.closedOffers, modeSetting, embargoState)
    }

    private onBeginnerHintReceived(data: BeginnerHintFormat) {
        if(data.type != GameAction.CreateTrade) return

        this.resetPreviouslySuggestedBeginnerTrade()
        this.suggestedBeginnerTrade = data.data as TradeCreationData
        if(this.container.parent == undefined) return

        this.onShowTradeUI()
    }

    private resetPreviouslySuggestedBeginnerTrade() {
        this.suggestedBeginnerTrade = undefined
        this.offeredCardSelectorView.beginnerCardAnimationController.stopBeginnerAnimation()
        this.wantedCardSelectorView.beginnerCardAnimationController.stopBeginnerAnimation()

        if(this.beginnerHintView == undefined) return

        this.beginnerHintView.destroy()
        this.beginnerHintView = undefined
    }

    private createBeginnerTradeHintView() {
        if(this.suggestedBeginnerTrade == undefined) return
        if(this.uiGameManager.gameController.isReplay) return
        if(this.beginnerHintView != undefined) return

        const resourceNames = this.getWantedResourceNames()
        const context = this.suggestedBeginnerTrade.isBankTrade ? 'tradeBank' : 'tradePlayers'
        const viewToShowHintAbove = this.suggestedBeginnerTrade.isBankTrade ? this.bankCheckButton : this.opponentsCheckButton

        if(this.container.parent == undefined) return
        if(viewToShowHintAbove.parent == undefined) return
        Sound.playSound(Sound.SoundType.BeginnerHintActivated)
        this.beginnerHintView = new UIGameBeginnerHintView(this.uiGameManager, viewToShowHintAbove, {key: 'strings:game.beginnerTips.actionHints.description',
            options: {context: context, resourceName: resourceNames}}, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade))
    }

    private getWantedResourceNames(): string {
        if(this.suggestedBeginnerTrade == undefined) {
            logError('getWantedResourceNames', [this.suggestedBeginnerTrade])
            return ''
        }

        let resourceNames = ''
        for(const [index, card] of this.suggestedBeginnerTrade.wantedResources.entries()) {
            resourceNames += CardEnum[card]
            if(index != this.suggestedBeginnerTrade.wantedResources.length - 1) resourceNames += ', '
        }
        return resourceNames
    }

    private onShowTradeUI() {
        this.uiGameManager.eventController.sendEvent(this.container, UIGameEventEnums.PopupActive)
        if(this.suggestedBeginnerTrade == undefined) return

        const requestedResources = this.suggestedBeginnerTrade.wantedResources
        const offeredResources = this.suggestedBeginnerTrade.offeredResources

        this.offeredCardSelectorView.beginnerCardAnimationController.startBeginnerAnimation(offeredResources)
        this.wantedCardSelectorView.beginnerCardAnimationController.startBeginnerAnimation(requestedResources)
    }

    private updateCardsForBeginnerAnimation() {
        if(this.suggestedBeginnerTrade == undefined) return

        const {offeredResources, wantedResources} = this.getCurrentTradeOffer(false)
        const correctResourcesAreOffered = this.offeredCardSelectorView.beginnerCardAnimationController.updateCardsForBeginnerAnimation(offeredResources)
        const correctResourcesAreWanted = this.wantedCardSelectorView.beginnerCardAnimationController.updateCardsForBeginnerAnimation(wantedResources)
        if(correctResourcesAreOffered && correctResourcesAreWanted) this.createBeginnerTradeHintView()
        if(correctResourcesAreWanted && this.isBeginnerMode) this.preventFurtherClicksForBeginnerMode(wantedResources)
    }

    toggleBankTradeMode(isEnabled: boolean) {
        this.inBankTradeMode = isEnabled

        this.offeredCardSelectorView.cardsContainer.setRatiosVisible(this.inBankTradeMode)
        this.proposalView.offeredCardPlayerInfo.cardContainer.setRatiosVisible(this.inBankTradeMode)
        this.wantedCardSelectorView.toggleBankTradeIcon(this.inBankTradeMode)

        if(this.inBankTradeMode) this.clearInvalidOfferedResourcesBank()

        const playersToTradeWith = this.proposalView.tradingTargetColors
        const proposalPlayerCards = this.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()
        const proposalWantedCards = this.proposalView.wantedCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()
        this.resetTradeWindow(proposalPlayerCards, proposalWantedCards, playersToTradeWith)
    }

    private clearInvalidOfferedResourcesBank() {
        const {offeredResources, wantedResources} = this.uiGameManager.tradeController.getCurrentTradeOffer(false)
        const tradeRatios = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.myColor).bankTradeRatiosState
        const bankTradeRatioError = TradeUtils.getBankTradeRatioError(offeredResources, wantedResources, tradeRatios)
        if(bankTradeRatioError == undefined) return

        this.removeCardsIfInvalidBankRatio()
    }

    private preventFurtherClicksForBeginnerMode(wantedResources: CardEnum[]) {
        this.wantedCardSelectorView.removeCardsClickAction(wantedResources)
        this.proposalView.wantedCardPlayerInfo.cardContainer.setCardClickAction((cardEnum: CardEnum) => undefined)
    }

    private removeCardsIfInvalidBankRatio() {
        const wantedCards = this.proposalView.wantedCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()
        const offeredCards = this.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.getAllCardEnums()

        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        for(const card of offeredCards) {
            const cardCount = ArrayUtils.countOccurenceOfValueInArray(offeredCards, card)
            const ratioForCard = TradeUtils.getBankTradeRatioForCard(bankTradeRatios, card)

            const isValidBankTradeRatio = cardCount % ratioForCard == 0
            if(isValidBankTradeRatio) continue

            this.uiGameManager.tradeController.proposalView.offeredCardPlayerInfo.cardContainer.removeCard(card)
            this.uiGameManager.tradeController.offeredCardSelectorView.cardsContainer.addNewCardOfType(card, false)
        }

        for(const card of wantedCards) {
            if(card != CardEnum.AnyResource) continue
            this.uiGameManager.tradeController.proposalView.wantedCardPlayerInfo.cardContainer.removeCard(card)
        }
    }

    private inCounterOffer(): boolean {
        return this.counterOfferTradeID != undefined
    }

    private doesPlayerHaveCards() {
        const cards = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.myColor).resourceCards.cards
        return cards.length == 0
    }
}
